/**
 * Parse the report that `claude -p "/usage" --output-format json` returns in its
 * `result` field (ENG-9394).
 *
 * WHY THIS EXISTS AT ALL, given `banner-parser.ts` already reads usage.
 *
 * The banner parser reads a status line scraped out of a RUNNING AGENT'S pane.
 * That signal has three defects this one does not, and they are the whole reason
 * ENG-9394 was filed:
 *
 *   1. It is per-agent, and the quantity is per-LOGIN. One host, one Claude
 *      login, a dozen agents - so a single fact produced thirteen criticals on
 *      2026-08-24.
 *   2. It cannot re-read. The banner is whatever last rendered in that agent's
 *      pane, so a reading taken once is presented indefinitely as current. On
 *      2026-08-24 all thirteen readings were over an hour stale and one was five
 *      hours stale, with nothing in the record saying so.
 *   3. The weekly form renders only an hour, not a date (ENG-7904), so the reset
 *      instant is a reconstruction that can never be more than 24h out. Nothing
 *      could auto-close against it, and ENG-8472 spent 11 days reading the
 *      resulting identical instants as evidence of a shared subscription.
 *
 * The print-mode report has none of those. It is a host-level fact, it can be
 * re-read on demand for zero tokens, and it names a DATE:
 *
 *   You are currently using your subscription to power your Claude Code usage
 *
 *   Current session: 16% used · resets Aug 25, 2:59am (UTC)
 *   Current week (all models): 95% used · resets Aug 25, 12:59am (UTC)
 *   Current week (Fable): 0% used
 *
 * PR #1307 (ENG-5443, May 2026) concluded that `claude -p "/usage"` does not
 * expand slash commands and that the pane scrape was therefore the only path.
 * That was true then and is false as of CLI 2.1.241, which is why this parser
 * can exist. Because it is a CLI-version fact rather than a contract, EVERY exit
 * here that fails to recognise the text returns null or a null field. A caller
 * must map that to "unknown" and must never map it to "at limit": failing closed
 * on a usage reading starves the fleet, which is strictly worse than the false
 * alert this replaces.
 *
 * TWO RENDER QUIRKS, both observed on a live host and both load-bearing:
 *
 *   - The reset string ROUNDS. The same window rendered "Aug 25, 1am (UTC)" at
 *     00:36Z and "Aug 25, 12:59am (UTC)" from 00:41Z onward. The two differ by a
 *     minute. Anything that dedupes or groups on the resolved instant must
 *     tolerate about a minute of jitter, or every poll will look like a brand
 *     new reset window.
 *   - A window line may carry NO reset at all (the per-model buckets do not),
 *     so `resetsAt` is nullable and its absence is not a parse failure.
 */
import { parseResetDateTime, RESET_DATE_SOURCE, type ResetPrecision } from './reset-time.js';

/** Which limit window a line reports. */
export type UsageWindowKind = 'session' | 'week';

export interface UsageWindowReading {
  kind: UsageWindowKind;
  /**
   * The parenthesised bucket exactly as rendered - `all models` for the weekly
   * aggregate, a model name such as `Fable` for a per-model bucket, and null for
   * the session line, which carries no bucket.
   */
  bucket: string | null;
  /** 0-100 inclusive. */
  pct: number;
  /**
   * When this window resets, or null when the line named no reset (per-model
   * buckets do not). Null is a legitimate reading, not a parse failure.
   */
  resetsAt: Date | null;
  /**
   * How `resetsAt` was obtained, or null when there was no reset to obtain.
   * Print mode has always rendered a date, so this should be `dated`; a
   * `hour_only` here means the CLI changed its render and the instant is a
   * reconstruction (see `reset-time.ts`).
   */
  resetPrecision: ResetPrecision | null;
}

export interface UsageReport {
  /**
   * Whether the CLI said this login is on a subscription. False means the report
   * rendered without that line - an API-key host, or a render we do not know.
   * Callers use this for the `claude_auth_mode != subscription` case, which must
   * record "not applicable" rather than 0% and rather than unknown.
   */
  subscription: boolean;
  /** The `Current session` line, or null when absent. */
  session: UsageWindowReading | null;
  /** The `Current week (all models)` aggregate, or null when absent. */
  week: UsageWindowReading | null;
  /** Weekly buckets that are NOT the aggregate, e.g. `Current week (Fable)`. */
  perModelWeek: UsageWindowReading[];
  /** Every window line recognised, in render order. */
  windows: UsageWindowReading[];
}

/** The aggregate bucket label, as the CLI renders it. */
const AGGREGATE_BUCKET = 'all models';

/**
 * Separator between the percentage and the reset clause. Mirrors the class in
 * `banner-parser.ts`: ASCII hyphen, en/em dash, and the U+00B7 middle dot the
 * CLI actually uses today.
 */
const SEP = /[\s·\-–—]+/.source;

/**
 * One usage line.
 *
 * Anchored per-line (`m` flag) rather than searched loose, because the same
 * words appear in the prose block below the readings ("What's contributing to
 * your limits usage?"). Anchoring is what stops this parser doing to itself what
 * ENG-8194 caught the banner parser doing - reading an agent's PROSE about usage
 * as a usage measurement.
 *
 * The trailing `$` also decides what happens to a line whose reset clause we do
 * NOT understand, and the choice is deliberate: the line is rejected whole. An
 * unintelligible reset clause means the CLI reworded its render, and keeping the
 * percentage while dropping the boundary would mint exactly the thing ENG-9394
 * exists to kill - a limit assertion with no instant to auto-close against
 * (ENG-7904's date-less banner, and ENG-8472's eleven days downstream of it). A
 * rejected line makes the reading unknown, which surfaces as a POLLER fault and
 * gates nothing.
 *
 * A line carrying NO reset clause is a different case and still parses: the
 * per-model buckets legitimately have none. Absent is a reading; unintelligible
 * is not.
 */
const WINDOW_LINE = new RegExp(
  `^\\s*Current\\s+(session|week)` +
    `(?:\\s*\\(([^)]*)\\))?` +
    `\\s*:\\s*(\\d{1,3})%\\s+used` +
    `(?:${SEP}resets\\s+(${RESET_DATE_SOURCE}))?\\s*$`,
  'gim',
);

/** The line the CLI prints when the login is a Claude subscription. */
const SUBSCRIPTION_LINE = /using\s+your\s+subscription\s+to\s+power\s+your\s+Claude\s+Code\s+usage/i;

/**
 * Parse a `/usage` report.
 *
 * Returns null when the text contains no recognisable window line at all, which
 * is the signal for "the CLI answered but not in a shape we know". That is a
 * poller fault - a version drift or a reworded render - and the caller must
 * record it as unknown and surface it as a problem with the POLLER, never as a
 * statement about the host's usage.
 */
export function parseUsageReport(text: string, now: Date = new Date()): UsageReport | null {
  if (typeof text !== 'string' || text.length === 0) return null;

  const windows: UsageWindowReading[] = [];
  const pattern = new RegExp(WINDOW_LINE.source, WINDOW_LINE.flags);
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index === pattern.lastIndex) pattern.lastIndex++;

    const kind = match[1]!.toLowerCase() as UsageWindowKind;
    const bucketRaw = match[2];
    const pct = Number.parseInt(match[3]!, 10);
    const resetStr = match[4];

    // A percentage outside 0-100 is not a reading we understand. Skip the line
    // rather than clamping it: a clamped 250% would silently become an at-limit
    // assertion, which is the one direction this parser must never invent.
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) continue;

    let resetsAt: Date | null = null;
    let resetPrecision: ResetPrecision | null = null;
    if (resetStr) {
      const resolved = parseResetDateTime(resetStr, now);
      // A reset clause we matched but cannot RESOLVE rejects the whole line
      // (CodeRabbit on #4988). The earlier draft kept the percentage with a null
      // boundary, which quietly collapsed two different facts into one value:
      // "this window has no reset clause" (the real post-reset render, which
      // must be trusted) and "this window named a reset we could not read"
      // (a grammar we no longer understand). They are indistinguishable
      // downstream, and the second silently produces an alert with nothing to
      // auto-close against - the defect this whole ticket exists to remove.
      //
      // Reachable rather than theoretical: "Feb 31" satisfies the grammar and
      // resolves to nothing.
      if (!resolved) continue;
      resetsAt = resolved.at;
      resetPrecision = resolved.precision;
    }

    windows.push({
      kind,
      bucket: bucketRaw === undefined ? null : bucketRaw.trim(),
      pct,
      resetsAt,
      resetPrecision,
    });
  }

  if (windows.length === 0) return null;

  const session = windows.find((w) => w.kind === 'session') ?? null;
  const weeks = windows.filter((w) => w.kind === 'week');
  const week =
    weeks.find((w) => (w.bucket ?? '').toLowerCase() === AGGREGATE_BUCKET) ??
    // Defensive: if the CLI ever drops the "(all models)" label from the
    // aggregate, a bucket-less weekly line is still the aggregate. A per-model
    // bucket always names its model, so this cannot capture one by accident.
    weeks.find((w) => w.bucket === null) ??
    null;
  const perModelWeek = weeks.filter((w) => w !== week);

  return {
    subscription: SUBSCRIPTION_LINE.test(text),
    session,
    week,
    perModelWeek,
    windows,
  };
}
